import {
    Collection,
    format,
    GlobalVariables,
    ICopySinkWithUpdateMethod,
    IModel,
    IValidationContext,
    IViolation,
    LibraryVariable,
    LinkedService,
    Metamodel,
    ModelConstants,
    Named,
    OwnedSet,
    shellStringResources,
    stringIsNullOrEmpty,
    timeoutValidationPatterns,
    TimespanValidator,
    UpdateMethod,
    UpdateMethodType
} from 'shell-models-lib';
import { CopyJobActivity, NullWatermarkBehavior, ReadMethod } from 'app/model/copyjob/copy-job.activity';
import { CopyJobMode, type CopyJobModeType, type IGenericChangeDataProperties } from 'app/model/copyjob/type';
import { GlobalVariables as NonShellGlobalVariables } from 'app/services/globals';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';

export const NEW_COPY_JOB_ACTIVITY_NAME = Math.random().toString(16).slice(2);

@Metamodel.model({
    name: ModelConstants.copyJob,
    display: [stringResources.CopyJob, stringResources.CopyJobs],
    reusable: true
})
export class CopyJob extends Named {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        display: stringResources.CopyJobMode,
        constraint: {
            validator: CopyJob.validateMode
        }
    })
    public mode: CopyJobModeType;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public source: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public sourceDmtsCredentialForArtifact: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public destination: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public destinationDmtsCredentialForArtifact: LinkedService;

    @Metamodel.attribute({
        type: CopyJobActivity.MODEL,
        collection: Collection.Set
    })
    public readonly activities = OwnedSet.create<CopyJobActivity>(this);

    @Metamodel.attribute({
        display: stringResources.Timeout,
        constraint: {
            // max timeout 7 days (7.00:00:00) or any 0-6 days combination with time
            patterns: GlobalVariables.activityRunTimeLimit ? timeoutValidationPatterns : undefined,
            pattern: GlobalVariables.activityRunTimeLimit
                ? undefined
                : new RegExp('^(\\d+\\.)?((0?\\d)|(1\\d)|(2[0-3]))(:[0-5]?\\d){2}$'),
            validator: (copyjob: CopyJob): IViolation[] | undefined => {
                if (copyjob.timeout && copyjob.timeoutDirty) {
                    try {
                        const timeoutInSeconds = TimespanValidator.toSeconds(copyjob.timeout);
                        if (timeoutInSeconds < 600) {
                            return [{ message: () => shellStringResources.Validator_MinTimeout }];
                        }
                    } catch {
                        return undefined;
                    }
                }
                return undefined;
            }
        }
    })
    public timeout: string = '0.12:00:00';

    @Metamodel.attribute({
        display: stringResources.Retry,
        constraint: {
            whole: true,
            minimum: 0
        }
    })
    public retry: number = 0;

    @Metamodel.attribute({
        display: stringResources.FullLoadBehavior
    })
    public fullLoadBehavior?: 'Truncate';

    @Metamodel.attribute({})
    public useAbap?: boolean;

    @Metamodel.attribute({})
    public dbNameForStagingStreams?: string;

    @Metamodel.attribute({})
    public schemaNameForStagingStreams?: string;

    @Metamodel.attribute({})
    public schemaNameForStagingTables?: string;

    @Metamodel.attribute({})
    public dataFactoryId?: string;

    @Metamodel.attribute({})
    public isCloneDeltaTable?: boolean;

    @Metamodel.attribute({})
    public useSharedCompute?: boolean;

    @Metamodel.attribute({})
    public genericChangeDataProperties?: IGenericChangeDataProperties;

    @Metamodel.attribute({
        type: LibraryVariable.MODEL,
        collection: Collection.Set
    })
    public readonly libraryVariables = OwnedSet.create<LibraryVariable>(this);

    public get firstActivity(): CopyJobActivity | undefined {
        return this.activities.values().next().value;
    }

    public get readMethod(): ReadMethod | undefined {
        return this.firstActivity?.readMethod;
    }

    public set readMethod(value: ReadMethod) {
        if (value != null && value !== this.readMethod) {
            for (const activity of this.activities.values()) {
                activity.readMethod = value;
            }
        }
    }

    public get nullWatermarkBehavior(): NullWatermarkBehavior {
        return this.firstActivity?.nullWatermarkBehavior ?? NullWatermarkBehavior.Skip;
    }

    public set nullWatermarkBehavior(value: NullWatermarkBehavior | undefined) {
        if (value == null || value !== this.nullWatermarkBehavior) {
            for (const activity of this.activities.values()) {
                activity.nullWatermarkBehavior = value;
            }
        }
    }

    public get isEmpty(): boolean {
        return this.source == null && this.destination == null && this.activities.size === 0;
    }

    public get isNativeCDCEnabled(): boolean | undefined {
        return this.firstActivity?.isNativeCDCEnabled;
    }

    @Metamodel.attribute({
        constraint: {
            validator: CopyJob.validateUpdateMethod
        }
    })
    public get updateMethod(): UpdateMethodType {
        return this.firstActivity?.updateMethod;
    }

    public get partitionOption(): string {
        return this.firstActivity?.partitionOption;
    }

    public static async validateMode(
        entity: CopyJob,
        mode: CopyJobModeType,
        _violation: IViolation | undefined,
        context: IValidationContext
    ): Promise<IViolation[]> {
        if (NonShellGlobalVariables.enableCJBatchUpsert) {
            return [];
        }
        const updateMethod = (entity.firstActivity?.copySink as ICopySinkWithUpdateMethod)?.updateMethod;
        if (stringIsNullOrEmpty(updateMethod)) {
            return [];
        }
        const sinkDataset = entity.firstActivity?.sinkDataset;
        const supportUpsertInBatch =
            updateMethod === UpdateMethod.Upsert &&
            mode === CopyJobMode.FullCopy &&
            sinkDataset != null &&
            (
                await getServiceInstance<DatasetRegistryService>(
                    context.contextData,
                    'DatasetRegistryService'
                )
            ).getDatasetConfiguration(Metamodel.modelOf(sinkDataset).name).copyJob?.updateMethod
                ?.supportUpsertInBatch;
        if (
            (updateMethod === UpdateMethod.SCD2 ||
                (updateMethod === UpdateMethod.Upsert && !supportUpsertInBatch)) &&
            mode === CopyJobMode.FullCopy
        ) {
            return [
                {
                    message: () =>
                        format(
                            stringResources.CopyJob_FullCopyDisabledTooltip,
                            stringResources.DataFlowDatasetUpdateMethod.toLocaleLowerCase()
                        )
                }
            ];
        }
        if (updateMethod === UpdateMethod.Overwrite && mode === CopyJobMode.IncrementalCopy) {
            return [
                {
                    message: () =>
                        format(
                            stringResources.CopyJob_IncrementalCopyDisabledTooltip,
                            stringResources.DataFlowDatasetUpdateMethod.toLocaleLowerCase()
                        )
                }
            ];
        }
        return [];
    }

    /**
     * Validates that the current update method is compatible with the CDC configuration.
     * SCD2 requires native CDC support (source + destination must both support it).
     *
     * For SCD2, `updateMethodSupportCDC()` is always true, so
     * `enableNativeCDC === supportCDCCopy`. Since `entity.isNativeCDCEnabled`
     * reflects `enableNativeCDC`, we can use it directly to check CDC support.
     */
    public static validateUpdateMethod(
        entity: CopyJob,
        updateMethod: UpdateMethodType
    ): IViolation[] {
        if (updateMethod === UpdateMethod.SCD2 && !entity.isNativeCDCEnabled) {
            return [{ message: () => stringResources.CopyJob_SCD2OnlySupportedInNativeCDCMessage }];
        }
        return [];
    }

    public addLibraryVariable(libraryVariable: LibraryVariable): void {
        if (this.libraryVariables.find((variable) => variable.name === libraryVariable.name) == null) {
            this.libraryVariables.add(libraryVariable);
        }
    }

    public getLibraryVariable(name: string) {
        return this.libraryVariables.find((variable) => variable.name === name);
    }

    public timeoutDirty?: boolean = false;
}
